import React from 'react';
import { createPortal } from 'react-dom';
import { Box, GlobalStyles } from '@mui/material';
import { PosSaleResult } from '../../reduxStore/PosSlice';
import { formatNumberWithSpaces } from '../../utils/utils';

interface PosTicketProps {
  sale:     PosSaleResult | null;
  caisse?:  string | null;
  mode?:    string | null;
  encaisse?: number | null;
  owner?:   { company?: string; email?: string; phone?: string } | null;
}

/**
 * Ticket d'encaissement — rendu caché à l'écran, visible uniquement à l'impression.
 * Appeler `window.print()` pour déclencher — le navigateur gère l'imprimante
 * (incluant les thermiques configurées comme imprimante par défaut).
 */
const PosTicket: React.FC<PosTicketProps> = ({ sale, caisse, mode, encaisse, owner }) => {
  if (!sale) return null;

  const { document, lignes, reglement } = sale;
  // total_ht / total_ttc sont des colonnes GENERATED renvoyées par la SP — source de vérité.
  // Fallback (prix_unitaire HT) uniquement si la SP ne les a pas fournis.
  const ligneHT = (l: typeof lignes[number]) =>
    l.total_ht ?? (l.quantite ?? 0) * (l.prix_unitaire ?? 0) * (1 - (l.remise ?? 0) / 100);
  const ligneTTC = (l: typeof lignes[number]) =>
    l.total_ttc ?? ligneHT(l) * (1 + (l.tva ?? 0) / 100);

  const totalTTC = lignes.reduce((acc, l) => acc + ligneTTC(l), 0);
  const totalHT  = lignes.reduce((acc, l) => acc + ligneHT(l), 0);
  const totalTVA = totalTTC - totalHT;
  const paye   = encaisse ?? reglement.montant ?? totalTTC;
  const rendu  = Math.max(0, paye - totalTTC);

  const ticket = (
    <>
      <GlobalStyles
        styles={{
          '@media print': {
            'html, body': {
              margin: 0,
              padding: 0,
              height: 'auto',
              minHeight: 0,
              background: '#fff',
            },
            // Masque complètement tout ce qui n'est pas le ticket
            'body > *:not(#pos-ticket)': { display: 'none !important' },
            '#pos-ticket': {
              display: 'block !important',
              width: '80mm',
              margin: 0,
              padding: '4mm',
              fontFamily: '"Courier New", monospace',
              fontSize: '11pt',
              color: '#000',
              background: '#fff',
              pageBreakAfter: 'avoid',
              breakAfter: 'avoid',
            },
            '@page': { size: '80mm auto', margin: 0 },
          },
        }}
      />

      <Box
        id="pos-ticket"
        sx={{ display: 'none' }}
      >
        {/* En-tête */}
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Box sx={{ fontSize: '14pt', fontWeight: 700 }}>
            {owner?.company ?? 'TICKET DE CAISSE'}
          </Box>
          {owner?.phone && <Box>{owner.phone}</Box>}
          {owner?.email && <Box>{owner.email}</Box>}
        </Box>

        <Box sx={{ borderTop: '1px dashed #000', my: 0.5 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>N° {document.numero_doc}</span>
          <span>{new Date(document.date_doc).toLocaleDateString()}</span>
        </Box>
        {caisse && <div>Caisse : {caisse}</div>}

        <Box sx={{ borderTop: '1px dashed #000', my: 0.5 }} />

        {/* Lignes */}
        {lignes.map((l, i) => {
          const ttc   = ligneTTC(l);
          return (
            <Box key={i} sx={{ mb: 0.5 }}>
              <Box>{l.article?.designation ?? l.article?.code ?? '—'}</Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '10pt' }}>
                <span>
                  {l.quantite} × {formatNumberWithSpaces(Number(l.prix_unitaire))}
                  {l.remise ? ` (-${l.remise}%)` : ''}
                </span>
                <span>{formatNumberWithSpaces(ttc)}</span>
              </Box>
            </Box>
          );
        })}

        <Box sx={{ borderTop: '1px dashed #000', my: 0.5 }} />

        {/* Totaux */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Total HT</span><span>{formatNumberWithSpaces(totalHT)}</span>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>TVA</span><span>{formatNumberWithSpaces(totalTVA)}</span>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '13pt' }}>
          <span>TOTAL TTC</span><span>{formatNumberWithSpaces(totalTTC)}</span>
        </Box>

        <Box sx={{ borderTop: '1px dashed #000', my: 0.5 }} />

        {/* Paiement */}
        {mode && <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Mode</span><span>{mode}</span>
        </Box>}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Payé</span><span>{formatNumberWithSpaces(paye)}</span>
        </Box>
        {rendu > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
            <span>Rendu</span><span>{formatNumberWithSpaces(rendu)}</span>
          </Box>
        )}

        <Box sx={{ borderTop: '1px dashed #000', my: 0.5 }} />

        <Box sx={{ textAlign: 'center', mt: 1, fontSize: '10pt' }}>
          Merci de votre visite
        </Box>
      </Box>
    </>
  );

  // Portal vers document.body → le ticket devient un frère direct du root React,
  // ce qui permet au sélecteur `body > *:not(#pos-ticket)` de masquer proprement
  // toute l'application lors de l'impression (sans page vide résiduelle).
  return createPortal(ticket, window.document.body);
};

export default PosTicket;
